import { useEffect, useState, useCallback } from 'react'
import axios from 'axios'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'

import { baseURL } from '../../config'

dayjs.extend(relativeTime)

function ExtensionErrors({ isDarkMode = true }) {
	const [errors, setErrors] = useState([])
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState(null)

	const [page, setPage] = useState(1)
	const [limit] = useState(30)
	const [pagination, setPagination] = useState(null)
	const [search, setSearch] = useState('')
	const [searchInput, setSearchInput] = useState('')
	const [errorType, setErrorType] = useState('')
	const [errorTypes, setErrorTypes] = useState([])
	const [startDate, setStartDate] = useState(dayjs().subtract(7, 'day').format('YYYY-MM-DD'))
	const [endDate, setEndDate] = useState(dayjs().format('YYYY-MM-DD'))

	const innerBg = isDarkMode ? 'bg-gray-800' : 'bg-white border border-gray-200'
	const statBg = isDarkMode ? 'bg-gray-700' : 'bg-gray-50'
	const inputClass = isDarkMode
		? 'bg-gray-700 text-white'
		: 'bg-white text-gray-900 border border-gray-300'
	const textHeading = isDarkMode ? 'text-white' : 'text-gray-900'
	const textBody = isDarkMode ? 'text-gray-300' : 'text-gray-600'
	const textLabel = isDarkMode ? 'text-gray-400' : 'text-gray-500'
	const textFaint = isDarkMode ? 'text-gray-500' : 'text-gray-400'
	const textRed = isDarkMode ? 'text-red-400' : 'text-red-600'
	const stackBg = isDarkMode ? 'bg-gray-900' : 'bg-gray-100'

	const fetchData = useCallback(async () => {
		setLoading(true)
		try {
			const userToken = localStorage.getItem('token')
			const params = new URLSearchParams({
				page: page.toString(),
				limit: limit.toString(),
				startDate,
				endDate,
			})
			if (search) params.append('search', search)
			if (errorType) params.append('errorType', errorType)

			const response = await axios.get(
				`${baseURL}/api/admin/extension/errors?${params}`,
				{ headers: { authorization: userToken } }
			)
			setErrors(response.data.data)
			setPagination(response.data.pagination)
			setErrorTypes(response.data.errorTypes || [])
		} catch (err) {
			setError(err)
		} finally {
			setLoading(false)
		}
	}, [page, limit, search, errorType, startDate, endDate])

	useEffect(() => {
		fetchData()
	}, [fetchData])

	const handleSearch = () => {
		setPage(1)
		setSearch(searchInput)
	}

	const handleErrorTypeChange = (type) => {
		setPage(1)
		setErrorType(type === errorType ? '' : type)
	}

	if (loading && errors.length === 0) {
		return <div className="text-center text-xl font-bold p-4">Loading...</div>
	}

	if (error) {
		return <div className="text-center text-red-500 p-4">Error: {error.message}</div>
	}

	return (
		<div className={`${isDarkMode ? 'bg-dark-color' : 'bg-white'} min-h-screen`}>
			<div className="p-4 space-y-3">
				<h1 className={`text-xl md:text-2xl font-bold ${textHeading}`}>
					Extension Errors ({pagination?.total || 0} total)
				</h1>

				<div className="flex flex-wrap gap-2 items-center">
					<input
						type="text"
						placeholder="Search email, error, stack..."
						value={searchInput}
						onChange={e => setSearchInput(e.target.value)}
						onKeyDown={e => e.key === 'Enter' && handleSearch()}
						className={`px-3 py-2 rounded ${inputClass} text-sm w-64`}
					/>
					<button
						onClick={handleSearch}
						className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
					>
						Search
					</button>
					<input
						type="date"
						value={startDate}
						onChange={e => { setStartDate(e.target.value); setPage(1) }}
						className={`px-3 py-2 rounded ${inputClass} text-sm`}
					/>
					<span className={textLabel}>to</span>
					<input
						type="date"
						value={endDate}
						onChange={e => { setEndDate(e.target.value); setPage(1) }}
						className={`px-3 py-2 rounded ${inputClass} text-sm`}
					/>
				</div>

				{errorTypes.length > 0 && (
					<div className="flex flex-wrap gap-2">
						{errorTypes.slice(0, 10).map((et) => (
							<button
								key={et.type}
								onClick={() => handleErrorTypeChange(et.type)}
								className={`px-3 py-1 rounded text-xs ${
									errorType === et.type
										? 'bg-red-600 text-white'
										: isDarkMode
											? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
											: 'bg-gray-100 text-gray-600 hover:bg-gray-200'
								}`}
							>
								{et.type} ({et.count})
							</button>
						))}
					</div>
				)}

				{pagination && (
					<div className="flex items-center gap-2">
						<button
							onClick={() => setPage(p => Math.max(1, p - 1))}
							disabled={page === 1}
							className={`px-3 py-1 ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'} disabled:opacity-50 disabled:cursor-not-allowed text-white rounded text-sm`}
						>
							Prev
						</button>
						<span className={`text-sm ${textLabel}`}>
							Page {pagination.page} of {pagination.totalPages}
						</span>
						<button
							onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
							disabled={page >= pagination.totalPages}
							className={`px-3 py-1 ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'} disabled:opacity-50 disabled:cursor-not-allowed text-white rounded text-sm`}
						>
							Next
						</button>
						{loading && <span className={`${textFaint} text-sm`}>Loading...</span>}
					</div>
				)}
			</div>

			<div className="p-4 space-y-2">
				{errors.map((err) => (
					<div
						key={err.id}
						className={`${innerBg} rounded-lg overflow-hidden p-4`}
					>
						<div className="flex flex-wrap justify-between items-start gap-2 mb-2">
							<div className="flex items-center gap-2">
								<span className="px-2 py-1 bg-red-600 text-white text-xs rounded">
									{err.element}
								</span>
								<span className={`px-2 py-1 ${statBg} ${textBody} text-xs rounded`}>
									{err.action}
								</span>
							</div>
							<span className={`${textLabel} text-xs`}>
								{dayjs(err.timestamp).format('MMM DD, YYYY hh:mm A')}
							</span>
						</div>

						<p className={`${textHeading} font-medium text-sm mb-2`}>
							{err.error}
						</p>

						<div className={`flex flex-wrap gap-4 text-xs ${textLabel} mb-3`}>
							<span>{err.userEmail}</span>
							{err.geoCity && err.geoCountry && (
								<span>{err.geoCity}, {err.geoCountry}</span>
							)}
							{err.clientIp && <span>IP: {err.clientIp}</span>}
						</div>

						{err.stack && (
							<div className={`${stackBg} rounded p-3 overflow-x-auto`}>
								<pre className={`text-xs ${textRed} whitespace-pre-wrap break-all`}>
									{err.stack}
								</pre>
							</div>
						)}
					</div>
				))}

				{errors.length === 0 && !loading && (
					<div className={`text-center ${textFaint} py-8`}>
						No errors found for the selected filters
					</div>
				)}
			</div>
		</div>
	)
}

export default ExtensionErrors
